import { Container, Graphics, Rectangle, Sprite, Texture } from "pixi.js"
import { emberEquipmentOf } from "game-common/ember_sprite/generate"
import { emberSpriteFor } from "game-common/ember_sprite/humanoids"
import { Facing } from "game-common/ember_sprite/raster"
import { entityHeldObjectSpriteId, inventoryHeldObjectSpriteId } from "game-common/item/item"
import { Entity, EntityId } from "game-common/models"
import { EMBER_LOAD_SIZE } from "./ember_composite_sprite"
import { BakedEmberAnimation, DrawnBounds, emberFrameBaker } from "./ember_frame_baker"
import { heldObjectTexture } from "./lpc_held_object"
import { LpcLayered } from "./lpc_layered"

/**
 * Where an LPC paperdoll's head sits from its origin: its 64px frame is centred there and the LPC
 * head is drawn around row 22. Widgets frame their crops on it (the roster cards, the run summary).
 */
export const LPC_PORTRAIT_HEAD = { x: 0, y: -10 }

/**
 * An Ember body is drawn at true proportions -- its head a third the size of LPC's big one -- so
 * a portrait zooms in on it to fill the same head-and-shoulders crop.
 */
export const EMBER_PORTRAIT_ZOOM = 1.9

/**
 * An LPC paperdoll's frame: 64px square, centred on its origin, an idle body's soles ending at y +30
 * (row 62). A whole-figure portrait takes the same box, so a screen laid out round one fits the other.
 */
export const LPC_FIGURE_BOX = { x: -32, y: -32, width: 64, height: 64 }
export const LPC_FIGURE_SOLES_Y = 30

/**
 * `head`: the head and shoulders, zoomed, for a card's crop (the roster, the scorecard). `figure`:
 * the whole body at its own size, weapon and all, standing in LPC_FIGURE_BOX (inventory, trade, the
 * conversation screens).
 */
export type PortraitFraming = "head" | "figure"

/**
 * A baked body's first frame cut down to the figure on it, so a portrait measures as the figure and
 * not the empty frame round it (80-128px, by what it holds). One per baked body, shared as its
 * frames are; it goes when the baker lets the body go.
 */
const figureCrops = new WeakMap<BakedEmberAnimation, Texture>()
const figureTexture = (baked: BakedEmberAnimation): Texture => {
    let crop = figureCrops.get(baked)
    if (!crop) {
        const { x, y, width, height } = baked.drawn[0]
        // Frame pixels, into the texture's own: the baker keeps only the drawn box (trimmed into the frame).
        const frame = baked.textures[0]
        const trim = frame.trim
        crop = new Texture(frame.baseTexture, trim ? new Rectangle(frame.frame.x + x - trim.x, frame.frame.y + y - trim.y, width, height) : new Rectangle(x, y, width, height))
        figureCrops.set(baked, crop)
    }
    return crop
}

/** What a widget needs of a still portrait, whichever body draws it. */
export type Paperdoll = Container & { isReadyToShow?: () => boolean; updatePaperdoll: (entity: Entity) => void }

/**
 * A still portrait of an Ember body: the entity's first idle frame, from the same
 * baked frames its sprite in the world uses, dressed in its equipment -- a drop-in for LpcLayered.
 * Framed on the head, it lands where an LPC paperdoll's head does; as a whole figure, it stands
 * where an LPC body stands in its frame.
 */
export class EmberPortrait extends Container {
    private sprite?: Sprite
    // What the arms carry (a readied load: wood, food, a wall piece), drawn between the hands as the
    // world sprite draws it (EmberCompositeSprite.updateLoad), so the portrait matches the body.
    private load?: Sprite
    private loadSpriteId?: string
    private released = false

    constructor(readonly entityId: EntityId, readonly framing: PortraitFraming = "head", readonly facing: Facing = "front") {
        super()
        if (framing === "figure") {
            // The frame bakes a few ticks later, but the screen measures its portrait now (to fit it
            // in a socket, or to lay out what follows): the box is there from the start, as LPC's is.
            const box = new Graphics()
            box.beginFill(0x000000, 0.001)
            box.drawRect(LPC_FIGURE_BOX.x, LPC_FIGURE_BOX.y, LPC_FIGURE_BOX.width, LPC_FIGURE_BOX.height)
            box.endFill()
            this.addChild(box)
        }
    }

    updatePaperdoll(entity: Entity): void {
        const name = emberSpriteFor(entity?.appearance)
        if (!name) return
        // A carried load puts the weapon away and holds the arms in front, as in the world. The pack
        // is the truth when the entity comes with one: a screen's appearance snapshot can carry a
        // stale held object (null) from before the hands were filled.
        this.loadSpriteId = (entity?.inventory?.items
            ? inventoryHeldObjectSpriteId(entity.inventory)
            : entityHeldObjectSpriteId(entity)) || undefined
        const equipment = this.loadSpriteId ? { ...emberEquipmentOf(entity), carry: true } : emberEquipmentOf(entity)
        // Screens rebuild their portrait on every message and keystroke: a body baked already is
        // drawn at once, or it would blink out for a frame each time.
        const action = `idle-${this.facing}` as const
        const baked = emberFrameBaker.peek(name, action, equipment)
        if (baked) {
            this.show(baked)
            return
        }
        emberFrameBaker.request(name, action, true, this.show, () => this.released, equipment)
    }

    private show = (baked: BakedEmberAnimation | null) => {
        if (!baked || this.released) return
        this.sprite?.destroy()
        this.sprite = undefined
        this.load?.destroy()
        this.load = undefined
        // Where frame pixel (0, 0) lands, and how big a frame pixel is, for placing the load after.
        let origin: { x: number; y: number }
        let scale: number
        if (this.framing === "figure") {
            if (!baked.drawn[0].width) return
            const drawn = baked.drawn[0]
            this.sprite = this.standInBox(new Sprite(figureTexture(baked)), drawn)
            scale = this.sprite.scale.x
            origin = { x: this.sprite.x - drawn.x * scale, y: this.sprite.y - drawn.y * scale }
        } else {
            const head = baked.joints[0].head
            this.sprite = new Sprite(baked.textures[0])
            this.sprite.scale.set(EMBER_PORTRAIT_ZOOM)
            this.sprite.position.set(LPC_PORTRAIT_HEAD.x - head.x * EMBER_PORTRAIT_ZOOM, LPC_PORTRAIT_HEAD.y - head.y * EMBER_PORTRAIT_ZOOM)
            scale = EMBER_PORTRAIT_ZOOM
            origin = { x: this.sprite.x, y: this.sprite.y }
        }
        this.addChild(this.sprite)
        this.showLoad(baked, origin, scale)
    }

    // Resting on the forearms: centred over the hands, most of it above them, its longest side
    // EMBER_LOAD_SIZE frame pixels -- the world sprite's own rule.
    private showLoad = (baked: BakedEmberAnimation, origin: { x: number; y: number }, scale: number) => {
        const texture = this.loadSpriteId ? heldObjectTexture(this.loadSpriteId) : undefined
        const hands = baked.joints[0]?.hands
        if (!texture || !hands) return
        this.load = new Sprite(texture)
        this.load.anchor.set(0.5, 0.75)
        this.load.scale.set((EMBER_LOAD_SIZE / Math.max(texture.orig.width, texture.orig.height)) * scale)
        this.load.position.set(
            origin.x + ((hands[0].x + hands[1].x) / 2) * scale,
            origin.y + ((hands[0].y + hands[1].y) / 2) * scale,
        )
        this.addChild(this.load)
    }

    /**
     * Soles on LPC's sole row, centred across, at its own size -- unless what it holds (a bow, a
     * polearm) makes the figure taller or wider than the box, when the whole of it is shrunk to fit.
     */
    private standInBox = (figure: Sprite, drawn: DrawnBounds): Sprite => {
        const box = LPC_FIGURE_BOX
        const scale = Math.min(1, (LPC_FIGURE_SOLES_Y - box.y) / drawn.height, box.width / drawn.width)
        figure.scale.set(scale)
        figure.position.set(
            Math.round(box.x + (box.width - drawn.width * scale) / 2),
            Math.round(LPC_FIGURE_SOLES_Y - drawn.height * scale),
        )
        return figure
    }

    isReadyToShow(): boolean {
        return !!this.sprite && !this.released
    }

    remove(): void {
        this.parent?.removeChild(this)
        this.destroy({ children: true })
    }

    destroy(options?: Parameters<Container["destroy"]>[0]): void {
        // The frames are the baker's, shared with every sprite of this body: never the textures.
        this.released = true
        super.destroy(typeof options === "object" ? { ...options, texture: false, baseTexture: false } : options)
    }
}

/**
 * A still portrait of an entity for a widget: its Ember body where it has one (emberSpriteFor),
 * else its LPC paperdoll. `staticPose` asks the LPC one for a single frame, as the Ember one is.
 */
export const paperdollFor = (entity: Entity, entityId: EntityId, staticPose = false, framing: PortraitFraming = "head", facing: Facing = "front"): Paperdoll => {
    const portrait: Paperdoll = emberSpriteFor(entity?.appearance)
        ? new EmberPortrait(entityId, framing, facing)
        : new LpcLayered(entityId, staticPose)
    portrait.updatePaperdoll(entity)
    return portrait
}
